'use client';

/**
 * ToolResultSkeleton
 *
 * Shown between `tool_call_start` and `tool_result` SSE chunks while a tool
 * is in flight. Visual rhythm matches Glass cards (rounded corners, faint
 * border) so the eventual rendered tool result lands in the same shape.
 *
 * Issue #1432 Phase 2
 */

import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { ShimmerLoader } from '../ShimmerLoader';

export interface ToolResultSkeletonProps {
  toolName?: string;
  /**
   * Optional override for the screen-reader label. When omitted the component
   * resolves `astorTools.skeleton.loading` via `useI18n()`. Pass a literal only
   * when calling outside an `I18nProvider` (e.g. some Storybook stories).
   */
  ariaLabel?: string;
}

/**
 * Skeleton displayed between `tool_call_start` and `tool_result` SSE chunks.
 * Issue #1432 Phase 2
 */
export const ToolResultSkeleton = ({ ariaLabel }: ToolResultSkeletonProps): ReactElement => {
  const { t } = useI18n();
  const label = ariaLabel ?? t('astorTools.skeleton.loading');
  return (
    <Box
      role="status"
      aria-label={label}
      data-testid="astor-tool-skeleton"
      sx={{
        my: 1.5,
        borderRadius: 2,
        p: 2,
        border: 1,
        borderColor: 'divider',
        width: '100%',
      }}
    >
      <ShimmerLoader variant="rectangular" width="40%" height={20} sx={{ mb: 1.5 }} />
      <ShimmerLoader variant="rectangular" width="100%" height={16} sx={{ mb: 1 }} />
      <ShimmerLoader variant="rectangular" width="100%" height={16} sx={{ mb: 1 }} />
      <ShimmerLoader variant="rectangular" width="80%" height={16} />
    </Box>
  );
};

export default ToolResultSkeleton;
